import React, { useState, useEffect } from 'react';
import BBALoader from '../BBALoader';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import { Target, Percent, Briefcase, DollarSign, TrendingUp, CalendarDays, RefreshCw, ShieldCheck, Zap, ArrowRight, AlertTriangle } from 'lucide-react';
import { FaArrowRight, FaCheckCircle, FaTimesCircle, FaPlus } from 'react-icons/fa';
import { useOnboarding } from '../../contexts/OnboardingContext';
import { useAuth } from '../../contexts/AuthContext';
import { useTheme } from '../../contexts/ThemeContext';
import { useSalesDashboard, SALES_TIMEFRAMES, salesTimeframeLabel } from '../../hooks/useSalesDashboard';
import { useDevMode } from '../../hooks/useDevMode';
import DevModePanel from '../dev/DevModePanel';
import { doc, updateDoc, getDoc, setDoc } from 'firebase/firestore';
import { db } from '../../firebase';
import MetricWidget from '../finance/MetricWidget';
import {
  ResponsiveContainer,
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
} from 'recharts';

const fmtAxisCurrency = (v) =>
  v >= 1000000 ? `$${(v / 1000000).toFixed(1)}M` : v >= 1000 ? `$${Math.round(v / 1000)}k` : `$${v}`;

const ConnectionPill = ({ connected, label }) => (
  <span
    className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium ${
      connected
        ? 'bg-emerald-50 text-emerald-700 dark:bg-emerald-900/20 dark:text-emerald-300'
        : 'bg-gray-100 text-gray-500 dark:bg-gray-700 dark:text-gray-400'
    }`}
  >
    {connected ? <FaCheckCircle className="text-xs" /> : <FaTimesCircle className="text-xs" />}
    {label}
  </span>
);

// Full-tab call-to-action shown when JobTread isn't connected — every metric
// on this tab is JobTread-sourced, so empty cards would just repeat themselves.
const ConnectJobTreadHero = () => (
  <div className="rounded-xl border bg-white dark:bg-gray-900 border-gray-200 dark:border-gray-800 shadow-sm px-6 py-12 md:py-16 text-center">
    <div className="w-20 h-20 mx-auto mb-5 rounded-2xl bg-gray-100 dark:bg-gray-700/60 flex items-center justify-center">
      <img src="/JTLogo.png" alt="JobTread" className="w-12 h-12 object-contain" />
    </div>
    <h3 className="text-xl md:text-2xl font-bold tracking-tight text-gray-900 dark:text-white mb-2">
      Connect JobTread to see your numbers
    </h3>
    <p className="text-sm text-gray-500 dark:text-gray-400 max-w-lg mx-auto mb-8">
      Your revenue, close ratio, average job size, and projects sold all flow straight
      from your JobTread account — live, with nothing to enter by hand.
    </p>
    <div className="flex flex-wrap items-center justify-center gap-x-6 gap-y-2 mb-8 text-xs font-medium text-gray-500 dark:text-gray-400">
      <span className="inline-flex items-center gap-1.5">
        <Zap className="w-3.5 h-3.5 text-red-600" /> Takes about a minute
      </span>
      <span className="inline-flex items-center gap-1.5">
        <RefreshCw className="w-3.5 h-3.5 text-red-600" /> Always up to date
      </span>
      <span className="inline-flex items-center gap-1.5">
        <ShieldCheck className="w-3.5 h-3.5 text-red-600" /> Secure grant-key access
      </span>
    </div>
    <Link
      to="/portal/profile#integrations"
      className="inline-flex items-center gap-2 px-6 py-3 bg-red-600 hover:bg-red-700 text-white rounded-lg text-sm font-semibold shadow-sm transition-colors"
    >
      Connect JobTread
      <ArrowRight className="w-4 h-4" />
    </Link>
  </div>
);

/** Customer-order status breakdown for the close ratio widget. */
const OrderStatusBar = ({ counts }) => {
  const other = counts?.other || 0;
  const total = (counts?.won || 0) + (counts?.open || 0) + (counts?.declined || 0) + other;
  if (total <= 0) return null;
  const pct = (n) => `${(n / total) * 100}%`;
  const rows = [
    { label: 'Approved / paid', value: counts.won, dot: 'bg-emerald-500' },
    { label: 'Pending / open / payable', value: counts.open, dot: 'bg-amber-400' },
    { label: 'Declined', value: counts.declined, dot: 'bg-rose-500' },
    // Drafts and any statuses outside the three buckets — shown, excluded from the ratio.
    ...(other > 0 ? [{ label: 'Other (not counted)', value: other, dot: 'bg-gray-400' }] : []),
  ];
  return (
    <div>
      <div className="flex h-2 rounded-full overflow-hidden bg-gray-200 dark:bg-gray-700">
        <div className="bg-emerald-500" style={{ width: pct(counts.won) }} />
        <div className="bg-amber-400" style={{ width: pct(counts.open) }} />
        <div className="bg-rose-500" style={{ width: pct(counts.declined) }} />
        {other > 0 && <div className="bg-gray-400" style={{ width: pct(other) }} />}
      </div>
      <div className="mt-2 space-y-1">
        {rows.map((r) => (
          <div key={r.label} className="flex items-center justify-between text-[11px] text-gray-500 dark:text-gray-400">
            <span className="flex items-center gap-1.5 min-w-0">
              <span className={`w-2 h-2 rounded-full shrink-0 ${r.dot}`} />
              {r.label}
            </span>
            <span className="font-semibold text-gray-700 dark:text-gray-200">{r.value}</span>
          </div>
        ))}
        {other > 0 && counts.otherStatuses && (
          <p className="text-[10px] text-gray-400 dark:text-gray-500 pl-3.5">
            {Object.entries(counts.otherStatuses)
              .sort((a, b) => b[1] - a[1])
              .map(([status, n]) => `${status}: ${n}`)
              .join(' · ')}
          </p>
        )}
      </div>
    </div>
  );
};

/** Simple closed vs open bar for close ratio widget */
const CloseRatioBar = ({ closed = 0, open = 0 }) => {
  const total = closed + open;
  if (total <= 0) return null;
  const closedPct = Math.round((closed / total) * 100);
  return (
    <div>
      <div className="flex h-2 rounded-full overflow-hidden bg-gray-200 dark:bg-gray-700">
        <div className="bg-emerald-500" style={{ width: `${closedPct}%` }} />
        <div className="bg-gray-300 dark:bg-gray-600 flex-1" />
      </div>
      <div className="flex justify-between text-[10px] text-gray-400 mt-1">
        <span>{closed} closed</span>
        <span>{open} open</span>
      </div>
    </div>
  );
};

// userId/accountId scope the analytics to a specific client (CRM Portal View
// embedding); when omitted, data loads for the logged-in portal user.
const SalesAnalyticsTab = ({ userId = null, accountId = null } = {}) => {
  const { darkMode } = useTheme();
  const { currentUser } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  // The uid whose data this tab shows — an explicit override or the viewer.
  const targetUid = userId || currentUser?.uid || null;

  const [timeframe, setTimeframe] = useState('ytd');
  const {
    loading: dataLoading,
    refresh,
    jobtreadConnected,
    jobtreadError,
    asOfDate,
    metrics,
    debug
  } = useSalesDashboard(timeframe, { jobtreadOnly: true, userId, accountId });
  const { devMode } = useDevMode();

  const [showAddPopup, setShowAddPopup] = useState(false);
  const [showEditPopup, setShowEditPopup] = useState(false);
  const [revenueEntries, setRevenueEntries] = useState([]);
  const [editingEntry, setEditingEntry] = useState(null);
  const [formData, setFormData] = useState({
    revenueAmount: '',
    month: new Date().toLocaleString('default', { month: 'long' }),
    year: new Date().getFullYear().toString()
  });

  const [pageLoading, setPageLoading] = useState(true);

  // Onboarding state
  const [currentStep, setCurrentStep] = useState('revenue');
  const [showSuccessMessage] = useState(false);

  const { isOnboardingMode, startOnboarding } = useOnboarding();

  const months = [
    'January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December'
  ];
  const years = Array.from({ length: 5 }, (_, i) => new Date().getFullYear() - 2 + i);

  // Manual entries stay available whenever JobTread isn't supplying revenue.
  const showRevenueManualEntry = metrics.revenue.source !== 'jobtread';

  useEffect(() => {
    if (!userId && currentUser && !isOnboardingMode && location.state?.fromOnboarding) {
      startOnboarding(currentUser.uid);
    }
  }, [userId, currentUser, isOnboardingMode, location.state, startOnboarding]);

  useEffect(() => {
    const loadUserData = async () => {
      if (!targetUid) {
        setPageLoading(false);
        return;
      }
      try {
        setPageLoading(true);
        const userDoc = await getDoc(doc(db, 'users', targetUid));
        if (userDoc.exists()) {
          const data = userDoc.data();
          if (data.revenueEntries) setRevenueEntries(data.revenueEntries);

          const salesData = data.salesData || {};

          if (isOnboardingMode) {
            if (salesData.closeRatio !== undefined || salesData.awardCount !== undefined) {
              setCurrentStep('completed');
            } else if (salesData.twoYearsAgo || salesData.oneYearAgo || salesData.currentYearProjection || data.revenueEntries?.length) {
              setCurrentStep('closeRatio');
            } else {
              setCurrentStep('revenue');
            }
          }
        }
      } catch (error) {
        console.error('Error loading user data:', error);
      } finally {
        setPageLoading(false);
      }
    };
    loadUserData();
  }, [targetUid, isOnboardingMode]);

  const handleAddRevenueEntry = async (e) => {
    e.preventDefault();
    if (!formData.revenueAmount || !formData.month || !formData.year) {
      alert('Please fill in all fields');
      return;
    }
    if (!targetUid) return;
    try {
      const newEntry = {
        id: Date.now(),
        amount: parseInt(formData.revenueAmount, 10),
        month: formData.month,
        year: parseInt(formData.year, 10),
        createdAt: new Date().toISOString()
      };
      setRevenueEntries((prev) => [...prev, newEntry]);
      const userDocRef = doc(db, 'users', targetUid);
      const userDoc = await getDoc(userDocRef);
      if (userDoc.exists()) {
        const currentEntries = userDoc.data().revenueEntries || [];
        await updateDoc(userDocRef, { revenueEntries: [...currentEntries, newEntry] });
      } else {
        await setDoc(userDocRef, { revenueEntries: [newEntry] });
      }
      setShowAddPopup(false);
      setFormData({
        revenueAmount: '',
        month: new Date().toLocaleString('default', { month: 'long' }),
        year: new Date().getFullYear().toString()
      });
      refresh();
    } catch (error) {
      console.error('Error adding entry:', error);
      alert('Error adding entry. Please try again.');
    }
  };

  const handleEditRevenueEntry = (entry) => {
    setEditingEntry(entry);
    setFormData({
      revenueAmount: entry.amount.toString(),
      month: entry.month,
      year: entry.year.toString()
    });
  };

  const handleUpdateRevenueEntry = async (e) => {
    e.preventDefault();
    if (!formData.revenueAmount || !formData.month || !formData.year || !targetUid) return;
    try {
      const updatedEntry = {
        ...editingEntry,
        amount: parseInt(formData.revenueAmount, 10),
        month: formData.month,
        year: parseInt(formData.year, 10),
        updatedAt: new Date().toISOString()
      };
      setRevenueEntries((prev) => prev.map((entry) => (entry.id === editingEntry.id ? updatedEntry : entry)));
      const userDocRef = doc(db, 'users', targetUid);
      const userDoc = await getDoc(userDocRef);
      if (userDoc.exists()) {
        const currentEntries = userDoc.data().revenueEntries || [];
        await updateDoc(userDocRef, {
          revenueEntries: currentEntries.map((entry) => (entry.id === editingEntry.id ? updatedEntry : entry))
        });
      }
      setEditingEntry(null);
      setFormData({
        revenueAmount: '',
        month: new Date().toLocaleString('default', { month: 'long' }),
        year: new Date().getFullYear().toString()
      });
      refresh();
    } catch (error) {
      console.error('Error updating entry:', error);
      alert('Error updating entry. Please try again.');
    }
  };

  const handleDeleteRevenueEntry = async (entryId) => {
    if (!targetUid) return;
    try {
      setRevenueEntries((prev) => prev.filter((entry) => entry.id !== entryId));
      const userDocRef = doc(db, 'users', targetUid);
      const userDoc = await getDoc(userDocRef);
      if (userDoc.exists()) {
        const currentEntries = userDoc.data().revenueEntries || [];
        await updateDoc(userDocRef, { revenueEntries: currentEntries.filter((entry) => entry.id !== entryId) });
      }
      refresh();
    } catch (error) {
      console.error('Error deleting entry:', error);
      alert('Error deleting entry. Please try again.');
    }
  };

  const isStepUnlocked = (step) => {
    if (!isOnboardingMode) return true;
    const stepOrder = ['revenue', 'closeRatio'];
    return stepOrder.indexOf(step) <= stepOrder.indexOf(currentStep);
  };

  const onboardingLocked = (step) => isOnboardingMode && !isStepUnlocked(step);

  const m = metrics;

  // Revenue chart series colors: won revenue green, open backlog yellow,
  // projected trend gray.
  const REVENUE_COLOR = '#10b981'; // emerald-500
  const BACKLOG_COLOR = '#eab308'; // yellow-500

  // Backlog (pending/open/payable orders) and projected-pace overlays on the
  // revenue chart — only rendered when the series actually carries them.
  const revSeries = m.revenue.series || [];
  const hasBacklogSeries = revSeries.some((p) => (p.backlog || 0) > 0);
  const hasProjectionSeries = revSeries.some((p) => p.projected != null);

  // Projects sold + average job size come from approved JobTread customer
  // orders (metrics.projectsSold / metrics.avgJobSize). Fallbacks: closed
  // leads behind the close ratio, then revenue ÷ projects sold as an estimate.
  const soldFromOrders = m.projectsSold?.rawValue ?? null;
  const closedCount = soldFromOrders ?? m.closeRatio.closedCount ?? null;
  const soldSub =
    soldFromOrders != null
      ? m.projectsSold.sub
      : m.closeRatio.source === 'jobtread'
      ? 'Closed jobs in JobTread'
      : "Closed leads you've tracked";
  const soldSource = soldFromOrders != null ? 'jobtread' : m.closeRatio.source;

  const avgFromOrders = m.avgJobSize?.rawValue ?? null;
  const avgFromJobs = m.closeRatio.avgJobSize ?? null;
  const avgFallback =
    (m.revenue.rawValue || 0) > 0 && closedCount > 0 ? m.revenue.rawValue / closedCount : null;
  const avgJobSize = avgFromOrders ?? avgFromJobs ?? avgFallback;
  const avgJobSizeSub =
    avgFromOrders != null
      ? m.avgJobSize.sub
      : avgFromJobs != null
      ? `Average of ${m.closeRatio.avgJobSizeCount} closed job${m.closeRatio.avgJobSizeCount === 1 ? '' : 's'} with values`
      : avgFallback != null
      ? 'Estimated: revenue ÷ projects sold'
      : null;
  const avgSource = avgFromOrders != null ? 'jobtread' : avgFromJobs != null ? m.closeRatio.source : m.revenue.source;

  if (pageLoading) {
    return (
      <div className="p-8">
        <div className="flex items-center justify-center min-h-[400px]">
          <BBALoader size={64} />
        </div>
      </div>
    );
  }

  // Every metric here is JobTread-sourced; when it's not connected (and we're not
  // in the manual onboarding flow) show the connect CTA instead of empty cards.
  const showConnectHero = !isOnboardingMode && !dataLoading && !jobtreadConnected;

  return (
    <div>
      <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4 mb-6">
        <div>
          <p className={`text-sm ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
            Your live sales picture, as of {asOfDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <ConnectionPill connected={jobtreadConnected} label="JobTread" />
          {!showConnectHero && (
          <select
            value={timeframe}
            onChange={(e) => setTimeframe(e.target.value)}
            title="Timeframe"
            className={`px-3 py-1.5 rounded-lg border text-sm font-medium focus:outline-none focus:ring-2 focus:ring-red-500 ${
              darkMode
                ? 'bg-gray-800 border-gray-700 text-gray-200'
                : 'bg-white border-gray-200 text-gray-700'
            }`}
          >
            {SALES_TIMEFRAMES.map((t) => (
              <option key={t.id} value={t.id}>
                {t.label}
              </option>
            ))}
          </select>
          )}
        </div>
      </div>

      {devMode && <DevModePanel debug={debug} page="sales" />}

      {!dataLoading && jobtreadConnected && jobtreadError && (
        <div className="flex items-start gap-3 bg-amber-50 dark:bg-amber-900/30 border border-amber-200 dark:border-amber-700 text-amber-800 dark:text-amber-200 px-4 py-3 rounded-lg mb-6 text-sm">
          <AlertTriangle className="w-5 h-5 shrink-0 mt-0.5" />
          <div>
            <p className="font-semibold">JobTread data couldn&apos;t be loaded</p>
            <p className="mt-0.5">{jobtreadError}</p>
          </div>
        </div>
      )}

      {showSuccessMessage && (
        <div className="bg-green-100 dark:bg-green-900 border border-green-200 dark:border-green-800 text-green-800 dark:text-green-200 px-4 py-3 rounded-lg mb-4">
          <strong className="font-bold">Success!</strong> Sales assessment saved.
        </div>
      )}

      {isOnboardingMode && currentStep === 'completed' && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50">
          <div className="bg-white dark:bg-gray-800 p-6 rounded-lg shadow-lg max-w-md w-full mx-4">
            <div className="text-center">
              <div className="flex items-center justify-center mb-4">
                <FaCheckCircle className="text-green-500 text-3xl mr-3" />
                <h3 className="text-xl font-semibold text-green-800 dark:text-green-200">Sales Assessment Completed!</h3>
              </div>
              <p className="text-green-700 dark:text-green-300 mb-4">
                You&apos;ve successfully completed your sales assessment. Now let&apos;s set your goals.
              </p>
              <button
                onClick={() => navigate('/portal/goals')}
                className="bg-green-600 hover:bg-green-700 text-white font-semibold py-3 px-6 rounded-lg shadow-lg transition-all duration-200"
              >
                Continue to Goals
                <FaArrowRight className="ml-2 inline" />
              </button>
            </div>
          </div>
        </div>
      )}

      {showConnectHero ? (
        <ConnectJobTreadHero />
      ) : (
      <>
      {/* Left 3/4: tall Revenue chart · Right 1/4: Avg Job Size / Projects Sold / Close Ratio */}
      <div className="grid grid-cols-1 xl:grid-cols-4 gap-5 mb-6">
        <div className="xl:col-span-3 flex flex-col [&>div]:flex-1">
        {/* Revenue */}
        <MetricWidget
          stretchChart
          title="Revenue"
          icon={<Target className="w-4 h-4" />}
          value={m.revenue.value}
          valueStatus={m.revenue.status}
          source={m.revenue.source}
          sub={m.revenue.sub}
          cue={m.revenue.cue}
          loading={dataLoading}
          empty={m.revenue.source === 'none'}
          emptyText="Approve customer orders in JobTread or add monthly revenue entries."
          emptyActionLabel="Add Revenue"
          trace={devMode ? m.revenue.trace : null}
          headerAction={
            <>
              {showRevenueManualEntry && !isOnboardingMode && (
                <button
                  onClick={() => setShowAddPopup(true)}
                  title="Add revenue entry"
                  className={`p-1 rounded-full ${darkMode ? 'bg-red-600 hover:bg-red-700 text-white' : 'bg-red-100 hover:bg-red-200 text-red-600'}`}
                >
                  <FaPlus className="w-3 h-3" />
                </button>
              )}
            </>
          }
        >
          {m.revenue.series?.length > 0 && (
            <>
              <div className="flex-1 min-h-[16rem]">
                <ResponsiveContainer width="100%" height="100%">
                  <AreaChart data={m.revenue.series} margin={{ top: 8, right: 8, left: 0, bottom: 0 }}>
                    <defs>
                      <linearGradient id="salesRevFill" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%" stopColor={REVENUE_COLOR} stopOpacity={0.25} />
                        <stop offset="95%" stopColor={REVENUE_COLOR} stopOpacity={0} />
                      </linearGradient>
                      <linearGradient id="salesBacklogFill" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%" stopColor={BACKLOG_COLOR} stopOpacity={0.22} />
                        <stop offset="95%" stopColor={BACKLOG_COLOR} stopOpacity={0} />
                      </linearGradient>
                    </defs>
                    <CartesianGrid strokeDasharray="3 3" vertical={false} stroke={darkMode ? '#374151' : '#e5e7eb'} />
                    <XAxis
                      dataKey="label"
                      tick={{ fontSize: 11, fill: darkMode ? '#9ca3af' : '#6b7280' }}
                      axisLine={false}
                      tickLine={false}
                    />
                    <YAxis
                      tickFormatter={fmtAxisCurrency}
                      tick={{ fontSize: 11, fill: darkMode ? '#9ca3af' : '#6b7280' }}
                      axisLine={false}
                      tickLine={false}
                      width={56}
                    />
                    <Tooltip
                      formatter={(v, name) => [`$${Number(v).toLocaleString()}`, name]}
                      contentStyle={
                        darkMode
                          ? { backgroundColor: '#1f2937', border: '1px solid #374151', borderRadius: 8, color: '#f9fafb' }
                          : { borderRadius: 8 }
                      }
                    />
                    <Area
                      type="monotone"
                      dataKey="value"
                      name="Revenue"
                      stackId="rev"
                      stroke={REVENUE_COLOR}
                      strokeWidth={2}
                      fill="url(#salesRevFill)"
                    />
                    {hasBacklogSeries && (
                      <Area
                        type="monotone"
                        dataKey="backlog"
                        name="Backlog (potential)"
                        stackId="rev"
                        stroke={BACKLOG_COLOR}
                        strokeWidth={1.5}
                        strokeDasharray="4 3"
                        fill="url(#salesBacklogFill)"
                      />
                    )}
                    {hasProjectionSeries && (
                      <Area
                        type="monotone"
                        dataKey="projected"
                        name="Projected trend"
                        stroke={darkMode ? '#9ca3af' : '#6b7280'}
                        strokeWidth={2}
                        strokeDasharray="6 4"
                        fill="transparent"
                      />
                    )}
                  </AreaChart>
                </ResponsiveContainer>
              </div>
              <div className="flex flex-wrap items-center gap-x-5 gap-y-1.5 mt-2 text-sm text-gray-600 dark:text-gray-300">
                <span className="flex items-center gap-2 font-medium">
                  <span className="w-3 h-3 rounded-full bg-emerald-500 shrink-0" />
                  Revenue
                </span>
                {hasBacklogSeries && m.revenue.backlog && (
                  <span className="flex items-center gap-2 font-medium">
                    <span className="w-3 h-3 rounded-full bg-yellow-500 shrink-0" />
                    Backlog: ${Math.round(m.revenue.backlog.value).toLocaleString()} across {m.revenue.backlog.count} open order{m.revenue.backlog.count === 1 ? '' : 's'}
                  </span>
                )}
                {hasProjectionSeries && (
                  <span className="flex items-center gap-2 font-medium">
                    <span className="w-4 border-t-2 border-dashed border-gray-500 dark:border-gray-400 shrink-0" />
                    Projected trend to year-end
                  </span>
                )}
                <span className="text-xs text-gray-400">{salesTimeframeLabel(timeframe)}</span>
              </div>
            </>
          )}
          {showRevenueManualEntry && revenueEntries.length > 0 && !isOnboardingMode && (
            <button
              onClick={() => setShowEditPopup(true)}
              className="text-xs text-red-600 hover:text-red-700 hover:underline mt-2"
            >
              Manage entries
            </button>
          )}
        </MetricWidget>
        </div>

        {/* Right column: Average Job Size, Projects Sold, Close Ratio */}
        <div className="flex flex-col gap-5 [&>div]:flex-1">
        {/* Average Job Size */}
        <MetricWidget
          title="Average Job Size"
          icon={<DollarSign className="w-4 h-4" />}
          value={avgJobSize != null ? `$${Math.round(avgJobSize).toLocaleString()}` : null}
          valueStatus="none"
          source={avgSource}
          sub={avgJobSizeSub}
          loading={dataLoading}
          empty={avgJobSize == null}
          emptyText="Approve customer orders in JobTread to see average job size."
        />

        {/* Projects Sold */}
        <MetricWidget
          title="Projects Sold"
          icon={<Briefcase className="w-4 h-4" />}
          value={closedCount != null ? closedCount.toLocaleString() : null}
          valueStatus="none"
          source={soldSource}
          sub={soldSub}
          loading={dataLoading}
          empty={closedCount == null}
          emptyText="Approve customer orders in JobTread to count sold projects."
        />

        {/* Close Ratio */}
        <MetricWidget
          title="Close Ratio"
          icon={<Percent className="w-4 h-4" />}
          value={onboardingLocked('closeRatio') ? null : m.closeRatio.value}
          valueStatus={m.closeRatio.status}
          source={m.closeRatio.source}
          sub={onboardingLocked('closeRatio') ? null : m.closeRatio.sub}
          loading={dataLoading}
          empty={onboardingLocked('closeRatio') || m.closeRatio.source === 'none'}
          emptyText={
            onboardingLocked('closeRatio')
              ? 'Complete your revenue assessment to unlock close ratio tracking.'
              : jobtreadConnected
              ? 'No customer orders in this timeframe yet.'
              : 'Connect JobTread for automatic conversion data.'
          }
          trace={devMode && !onboardingLocked('closeRatio') ? m.closeRatio.trace : null}
        >
          {!onboardingLocked('closeRatio') &&
            (m.closeRatio.orderCounts ? (
              <OrderStatusBar counts={m.closeRatio.orderCounts} />
            ) : (
              m.closeRatio.closedCount != null && (
                <CloseRatioBar closed={m.closeRatio.closedCount} open={m.closeRatio.openCount} />
              )
            ))}
        </MetricWidget>
        </div>
      </div>

      {/* Projections: next quarter + year-end revenue at the current pace */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-5 mb-6">
        <MetricWidget
          title="Next Quarter Projection"
          icon={<TrendingUp className="w-4 h-4" />}
          value={m.nextQuarterProjection?.value}
          valueStatus="none"
          source={m.nextQuarterProjection?.source || 'none'}
          sub={m.nextQuarterProjection?.sub}
          cue={m.nextQuarterProjection?.cue}
          loading={dataLoading}
          empty={(m.nextQuarterProjection?.source || 'none') === 'none'}
          emptyText="Approve customer orders in JobTread this year to project next quarter."
          trace={devMode ? m.nextQuarterProjection?.trace : null}
        />
        <MetricWidget
          title={`${asOfDate.getFullYear()} Revenue Projection`}
          icon={<CalendarDays className="w-4 h-4" />}
          value={m.yearProjection?.value}
          valueStatus="none"
          source={m.yearProjection?.source || 'none'}
          sub={m.yearProjection?.sub}
          cue={m.yearProjection?.cue}
          loading={dataLoading}
          empty={(m.yearProjection?.source || 'none') === 'none'}
          emptyText="Approve customer orders in JobTread this year to project year-end revenue."
          trace={devMode ? m.yearProjection?.trace : null}
        />
      </div>
      </>
      )}

      {/* Add Revenue Entry Popup */}
      {showAddPopup && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50">
          <div className={`bg-white dark:bg-gray-900 rounded-2xl shadow-xl border border-gray-200 dark:border-gray-800 p-6 w-full max-w-md`}>
            <div className="flex items-center mb-4 relative">
              <h3 className={`text-lg font-semibold flex-1 text-center ${darkMode ? 'text-white' : 'text-gray-800'}`}>Add Revenue Entry</h3>
              <button onClick={() => setShowAddPopup(false)} className={`p-1 rounded-full absolute right-0 ${darkMode ? 'hover:bg-gray-700' : 'hover:bg-gray-100'}`}>
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            <form onSubmit={handleAddRevenueEntry} className="space-y-4">
              <div>
                <label className={`block text-sm font-medium mb-2 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>Revenue Amount</label>
                <div className="relative">
                  <span className="absolute left-3 top-3 text-gray-500">$</span>
                  <input
                    type="number"
                    value={formData.revenueAmount}
                    onChange={(e) => setFormData({ ...formData, revenueAmount: e.target.value })}
                    className={`pl-7 w-full p-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500 ${darkMode ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300'}`}
                    placeholder="Enter revenue amount"
                    min="0"
                  />
                </div>
              </div>
              <div>
                <label className={`block text-sm font-medium mb-2 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>Month</label>
                <select
                  value={formData.month}
                  onChange={(e) => setFormData({ ...formData, month: e.target.value })}
                  className={`w-full p-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500 ${darkMode ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300'}`}
                >
                  {months.map((month) => <option key={month} value={month}>{month}</option>)}
                </select>
              </div>
              <div>
                <label className={`block text-sm font-medium mb-2 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>Year</label>
                <select
                  value={formData.year}
                  onChange={(e) => setFormData({ ...formData, year: e.target.value })}
                  className={`w-full p-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500 ${darkMode ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300'}`}
                >
                  {years.map((year) => <option key={year} value={year}>{year}</option>)}
                </select>
              </div>
              <div className="flex space-x-3 pt-4">
                <button type="button" onClick={() => setShowAddPopup(false)} className={`flex-1 py-2 px-4 rounded-lg border ${darkMode ? 'border-gray-600 text-gray-300 hover:bg-gray-700' : 'border-gray-300 hover:bg-gray-50'}`}>Cancel</button>
                <button type="submit" className="flex-1 py-2 px-4 bg-red-600 text-white rounded-lg hover:bg-red-700">Add Entry</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Edit Revenue Entries Popup */}
      {showEditPopup && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50">
          <div className={`bg-white dark:bg-gray-900 rounded-2xl shadow-xl border border-gray-200 dark:border-gray-800 p-6 w-full max-w-2xl mx-4 max-h-[80vh] overflow-y-auto`}>
            <div className="flex items-center mb-4 relative">
              <h3 className={`text-lg font-semibold flex-1 text-center ${darkMode ? 'text-white' : 'text-gray-800'}`}>Manage Revenue Entries</h3>
              <button
                onClick={() => { setShowEditPopup(false); setEditingEntry(null); }}
                className={`p-1 rounded-full absolute right-0 ${darkMode ? 'hover:bg-gray-700' : 'hover:bg-gray-100'}`}
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            {editingEntry && (
              <div className={`p-4 mb-4 rounded-xl border border-gray-100 dark:border-gray-800 bg-gray-50 dark:bg-gray-800/50`}>
                <h4 className={`font-medium mb-3 ${darkMode ? 'text-white' : 'text-gray-800'}`}>Edit Entry</h4>
                <form onSubmit={handleUpdateRevenueEntry} className="grid grid-cols-1 sm:grid-cols-4 gap-4">
                  <div>
                    <label className={`block text-sm font-medium mb-1 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>Amount</label>
                    <div className="relative">
                      <span className="absolute left-3 top-2 text-gray-500">$</span>
                      <input
                        type="number"
                        value={formData.revenueAmount}
                        onChange={(e) => setFormData({ ...formData, revenueAmount: e.target.value })}
                        className={`pl-7 w-full p-2 border rounded focus:outline-none focus:ring-2 focus:ring-red-500 ${darkMode ? 'bg-gray-600 border-gray-500 text-white' : 'bg-white border-gray-300'}`}
                        min="0"
                      />
                    </div>
                  </div>
                  <div>
                    <label className={`block text-sm font-medium mb-1 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>Month</label>
                    <select
                      value={formData.month}
                      onChange={(e) => setFormData({ ...formData, month: e.target.value })}
                      className={`w-full p-2 border rounded focus:outline-none focus:ring-2 focus:ring-red-500 ${darkMode ? 'bg-gray-600 border-gray-500 text-white' : 'bg-white border-gray-300'}`}
                    >
                      {months.map((month) => <option key={month} value={month}>{month}</option>)}
                    </select>
                  </div>
                  <div>
                    <label className={`block text-sm font-medium mb-1 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>Year</label>
                    <select
                      value={formData.year}
                      onChange={(e) => setFormData({ ...formData, year: e.target.value })}
                      className={`w-full p-2 border rounded focus:outline-none focus:ring-2 focus:ring-red-500 ${darkMode ? 'bg-gray-600 border-gray-500 text-white' : 'bg-white border-gray-300'}`}
                    >
                      {years.map((year) => <option key={year} value={year}>{year}</option>)}
                    </select>
                  </div>
                  <div className="flex items-end space-x-2">
                    <button type="submit" className="px-4 py-2 bg-green-600 text-white rounded hover:bg-green-700">Update</button>
                    <button type="button" onClick={() => setEditingEntry(null)} className="px-4 py-2 bg-gray-500 text-white rounded hover:bg-gray-600">Cancel</button>
                  </div>
                </form>
              </div>
            )}

            <div className="space-y-2">
              <h4 className={`font-medium mb-3 ${darkMode ? 'text-white' : 'text-gray-800'}`}>All Entries</h4>
              {revenueEntries.length === 0 ? (
                <p className={`text-center py-4 ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>No entries found. Add your first entry using the + button.</p>
              ) : (
                revenueEntries
                  .slice()
                  .sort((a, b) => (b.year - a.year) || (months.indexOf(b.month) - months.indexOf(a.month)))
                  .map((entry) => (
                    <div key={entry.id} className={`flex items-center justify-between p-3 rounded-xl border border-gray-100 dark:border-gray-800 bg-gray-50 dark:bg-gray-800/50`}>
                      <div className="flex items-center space-x-4">
                        <span className={`font-medium ${darkMode ? 'text-white' : 'text-gray-800'}`}>${entry.amount.toLocaleString()}</span>
                        <span className={`text-sm ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>{entry.month} {entry.year}</span>
                      </div>
                      <div className="flex space-x-2">
                        <button onClick={() => handleEditRevenueEntry(entry)} className={`p-1 rounded ${darkMode ? 'hover:bg-gray-600' : 'hover:bg-gray-200'}`}>
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                          </svg>
                        </button>
                        <button onClick={() => handleDeleteRevenueEntry(entry.id)} className="p-1 rounded text-red-500 hover:bg-red-100 dark:hover:bg-red-900">
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                          </svg>
                        </button>
                      </div>
                    </div>
                  ))
              )}
            </div>
          </div>
        </div>
      )}

    </div>
  );
};

export default SalesAnalyticsTab;
